import styles from '@/app/styles/components/tooltip.module.css'
import Text, { TextVariant } from '@/components/atomic/text'
import { globalLogger } from '@/lib/logger'
import { cn } from '@/utils/shadcn/utils'

import { Tooltip as TooltipBase } from './base'

type Alignment = 'start' | 'center' | 'end'

type Side = 'top' | 'right' | 'bottom' | 'left'

// TODO: as part of design system migration, we should try to follow the advice:
// "make illegal states unrepresentable".
// Currently, illegal states (specify BOTH or NEITHER customContent, simpleText)
// Result in runtime errors when they really amount to a build-time misconfiguration.
export interface TooltipProps {
  align?: Alignment
  children: React.ReactNode
  className?: string
  customContent?: React.ReactNode
  defaultOpen?: boolean
  disabled?: boolean // When true, tooltip will not show
  maxWidth?: string | number // Custom max width (e.g., '300px', 300, 'none')
  open?: boolean
  onOpenChange?: (open: boolean) => void
  side?: Side
  sideOffset?: number // in px
  simpleText?: string
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function Tooltip({
  align = 'center',
  children,
  className = '',
  customContent,
  defaultOpen,
  disabled = false,
  maxWidth = '200px',
  open,
  onOpenChange,
  side = 'top',
  sideOffset = 5, // in px
  simpleText = '',
  ...props
}: TooltipProps) {
  const ContentWrapper = ({
    customContent,
    simpleText,
  }: {
    customContent: React.ReactNode
    simpleText?: string
  }) => {
    if (!customContent && !simpleText) {
      if (process.env.NODE_ENV !== 'production') {
        globalLogger.error(
          'Tooltip requires either customContent or simpleText to be defined.',
          {
            error: new Error(
              'Tooltip requires either customContent or simpleText to be defined.',
            ),
          },
        )
      }
      // This behavior is likely incorrect, likely makes more sense to still return children.
      return null
    }

    if (customContent && simpleText) {
      if (process.env.NODE_ENV !== 'production') {
        globalLogger.warn(
          'Both customContent and simpleText are defined. customContent will take precedence.',
        )
      }
    }

    // Handle maxWidth prop - use inline styles for dynamic values
    const maxWidthValue =
      maxWidth === 'none'
        ? 'none'
        : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
          typeof maxWidth === 'number'
          ? `${maxWidth}px`
          : maxWidth

    return (
      <div
        className={cn(
          'px-2 py-1.5',
          'rounded-md',
          'pointer-events-none',
          styles['moment-tooltip'],
          className,
        )}
        style={{ maxWidth: maxWidthValue }}
      >
        {customContent ? (
          customContent
        ) : (
          /* omitWrapper: the skeleton wrapper's min-w-16 pads labels
             shorter than 64px, leaving the text hugging the left edge
             of the tooltip. Tooltips never render a loading state. */
          <Text variant={TextVariant.BodyXS} omitWrapper>
            {simpleText}
          </Text>
        )}
      </div>
    )
  }

  // If disabled, just render children without tooltip wrapper
  if (disabled) {
    return <>{children}</>
  }

  return (
    <TooltipBase
      align={align}
      content={
        <ContentWrapper simpleText={simpleText} customContent={customContent} />
      }
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={onOpenChange}
      side={side}
      sideOffset={sideOffset}
      {...props}
    >
      {children}
    </TooltipBase>
  )
}
